<template>
  <div class="notice-container">
    <!-- 顶部标题 -->
    <NavBar :title="t('notice.title')" :show-back="false" />
    <!-- 导航栏 -->
    <div class="notice-tabs">
      <div class="tab-container">
        <div
          v-for="tab in tabs"
          :key="tab.id"
          class="tab"
          :class="{ active: userStore.curNoticeTab === tab.id }"
          @click="handleTabClick(tab.id)"
        >
          <span class="tab-text">{{ $t(tab.nameKey) }}</span>
          <!-- 未读消息气泡 -->
          <div v-if="getUnreadCount(tab.id) > 0" class="unread-badge">
            {{ getUnreadCount(tab.id) > 99 ? "99+" : getUnreadCount(tab.id) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 下拉刷新指示器 -->
    <div
      class="pull-refresh-indicator transform-transition"
      :class="{
        'is-refreshing': isRefreshing,
      }"
      :style="{ transform: `translateY(${pullDistance}px)` }"
    >
      <div class="refresh-icon">
        <div class="spinner" v-if="isRefreshing"></div>
      </div>
    </div>

    <!-- 内容区 -->
    <div class="notice-content-wrapper">
      <transition name="fade" mode="out-in">
        <div
          v-if="userStore.notificationDetail.length === 0"
          class="empty-wrapper"
          key="empty"
        >
          <Empty :tip="$t('notice.empty')" />
        </div>
        <div
          v-else
          class="notice-content transform-transition"
          key="content"
          :style="{ transform: `translateY(${pullDistance}px)` }"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <transition-group name="list" tag="div">
            <NoticeItem
              v-for="(item, index) in userStore.notificationDetail"
              :key="item.id"
              :notification="item"
              :type="userStore.curNoticeTab"
              @update:notification="handleNotificationUpdate(index, $event)"
            />
          </transition-group>
          <!-- 加载更多状态 -->
          <div v-if="userStore.notificationLoadingMore" class="load-more">
            <Loading :text="t('common.loading')" type="text" />
          </div>
          <!-- 没有更多数据 -->
          <div
            v-else-if="!userStore.notificationPagination?.has_next"
            class="no-more"
          >
            {{ t("common.noMore") }}
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, onActivated, nextTick, onMounted, onUnmounted } from "vue";
import { useUserStore, useToastStore } from "@/stores";
import { NoticeTab } from "@/stores/user";
// @ts-ignore
import NavBar from "@/components/common/NavBar.vue";
// @ts-ignore
import Empty from "@/components/common/Empty.vue";
// @ts-ignore
import Loading from "@/components/common/Loading.vue";
// @ts-ignore
import NoticeItem from "@/components/notice/NoticeItem.vue";
import { clickButton, pageType } from "@/utils/point";

const { t } = useI18n();
const userStore = useUserStore();
const toast = useToastStore();

// 下拉刷新相关状态
const isRefreshing = ref(false);
const pullDistance = ref(0);
const startY = ref(0);
const currentY = ref(0);
const pullThreshold = 80; // 下拉触发阈值
const maxPullDistance = 120; // 最大下拉距离

const tabs = [
  {
    id: NoticeTab.Notification,
    nameKey: "notice.tabs.notification",
  },
  {
    id: NoticeTab.Reply,
    nameKey: "notice.tabs.reply",
  },
  {
    id: NoticeTab.Mention,
    nameKey: "notice.tabs.mention",
  },
  {
    id: NoticeTab.Like,
    nameKey: "notice.tabs.like",
  },
] as const;

const handleTabClick = async (tabId: NoticeTab) => {
  clickButton({
    eventType: pageType.NOTICE,
    eventReason: "切换消息分类",
    eventValue: JSON.stringify({
      tab: tabId,
    }),
  });
  await userStore.getNotificationDetail(tabId);
  userStore.setCurNoticeTab(tabId);
};

// 处理通知更新
const handleNotificationUpdate = (index: number, updatedNotification: any) => {
  // 使用 userStore 的方法更新通知项
  userStore.updateNotificationItem(index, updatedNotification);
};

// 获取对应tab的未读数量
const getUnreadCount = (tabId: NoticeTab): number => {
  switch (tabId) {
    case NoticeTab.Notification:
      return userStore.notifyCount;
    case NoticeTab.Reply:
      return userStore.postUnreadCount;
    case NoticeTab.Mention:
      return userStore.mentionUnreadCount;
    case NoticeTab.Like:
      return userStore.likeUnreadCount;
    default:
      return 0;
  }
};

// 下拉刷新相关方法
const handleTouchStart = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  startY.value = e.touches[0].clientY;
  currentY.value = startY.value;
};

const handleTouchMove = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  currentY.value = e.touches[0].clientY;
  const deltaY = currentY.value - startY.value;

  // 只处理向下滑动
  if (deltaY > 0) {
    e.preventDefault();
    const distance = Math.min(deltaY * 0.5, maxPullDistance);
    pullDistance.value = distance;
  }
};

const handleTouchEnd = async () => {
  if (pullDistance.value > pullThreshold && !isRefreshing.value) {
    // 触发刷新
    await handleRefresh();
  } else {
    // 重置状态
    resetPullState();
  }
};

const handleRefresh = async () => {
  isRefreshing.value = true;
  pullDistance.value = pullThreshold;

  try {
    // 重新获取当前tab的通知列表
    await userStore.getNotificationDetail(userStore.curNoticeTab);
    await userStore.getNotificationCount();
    toast.showToast(t("common.refreshSuccess"), "success", 1000);
  } catch (error) {
    console.error("刷新失败:", error);
    toast.showToast(t("common.refreshFailed"), "error", 1000);
  } finally {
    // 延迟重置状态，让用户看到成功提示
    setTimeout(() => {
      resetPullState();
    }, 1000);
  }
};

const resetPullState = () => {
  pullDistance.value = 0;
  isRefreshing.value = false;
};

// 滚动监听处理
const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const windowHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;

  // 当滚动到底部附近时加载更多
  if (documentHeight - scrollTop - windowHeight < 100) {
    loadMore();
  }
};

// 加载更多
const loadMore = async () => {
  if (
    !userStore.notificationLoadingMore &&
    userStore.notificationPagination?.has_next
  ) {
    await userStore.loadMoreNotifications();
  }
};

// 组件挂载时添加滚动监听
onMounted(() => {
  if (!userStore.checkLogin()) {
    return;
  }
  window.addEventListener("scroll", handleScroll);
  window.scrollTo({ top: 0, behavior: "smooth" });
});

// 组件卸载时移除滚动监听
onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

onActivated(async () => {
  const uid = userStore.userInfo?.uid;
  if (uid) {
    await userStore.getNotificationDetail(userStore.curNoticeTab);
  }
});
</script>

<style scoped>
.notice-container {
  background-color: #f4f6fa;
  min-height: 100vh;
  padding: 56px 0 100px;
  position: relative;
}

/* 下拉刷新指示器样式 */
.pull-refresh-indicator {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: transform 0.3s ease;
  z-index: 1;
  pointer-events: none;
}

.pull-refresh-indicator .refresh-icon {
  width: 20px;
  height: 20px;
  margin-bottom: 4px;
  position: relative;
}

.pull-refresh-indicator .spinner {
  width: 100%;
  height: 100%;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #dd8632;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.empty-wrapper {
  margin-top: 100px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.notice-header {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  padding: 12px 0;
  border-bottom: 1px solid #f2f2f2;
}

.notice-tabs {
  background-color: #fff;
  padding: 10px 0;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 10;
}

.tab-container {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
}

.tab {
  font-size: 14px;
  color: #999;
  padding: 12px 0 8px 0;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.tab.active {
  color: #222;
  font-weight: bold;
}

.tab.active::after {
  content: "";
  display: block;
  width: 60px;
  height: 4px;
  background: #dd8632;
  border-radius: 2px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -2px;
  transition: all 0.3s ease;
}

.tab-text {
  position: relative;
}

.unread-badge {
  position: absolute;
  top: 4px;
  right: -15px;
  min-width: 16px;
  height: 16px;
  background-color: #df4c6e;
  color: white;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
}

.notice-content {
  max-width: 800px;
  margin: 10px auto 0;
  padding: 0 10px 100px;
  margin-top: 80px;
}

.load-more {
  text-align: center;
  padding: 20px 0;
}

.no-more {
  text-align: center;
  color: #999;
  font-size: 14px;
  padding: 20px 0;
}

/* 删除所有过渡动画相关样式 */
</style>
